// StablefordCard — live standings for a Stableford side game.
//
// Standard points transform per hole (eagle+ = 4, birdie = 3, par = 2,
// bogey = 1, double+ = 0 for gross Stableford). Winner-take-all pot at
// unit_value × (N - 1); ties split.
//
// Read-only display of get_stableford_state:
//   • Leaderboard tile per player: total points, running units.
//   • Per-hole grid showing points per player (color-coded by band).

import { useCallback, useEffect, useState } from 'react'
import { db } from '../lib/db'

export default function StablefordCard({ game_id, autoRefreshMs = 0 }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const { data } = await db('get_stableford_state', { game_id })
      setState(data)
      setError(data?.error || null)
    } catch (e) {
      setError(e.message || 'Stableford compute failed')
    }
    if (!silent) setLoading(false)
  }, [game_id])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!autoRefreshMs) return
    const t = setInterval(() => load({ silent: true }), autoRefreshMs)
    return () => clearInterval(t)
  }, [autoRefreshMs, load])

  if (loading && !state) return <p className="text-muted text-sm">Loading Stableford…</p>
  if (error && !state) return <p style={{ color: 'var(--red)' }}>{error}</p>
  if (!state) return null

  const { participants = [], holes = [], totals = {} } = state
  const nameFor = (pid) => participants.find(p => p.player_id === pid)?.name || pid.slice(0, 6)

  const ranked = [...participants]
    .map(p => ({
      ...p,
      points: totals[p.player_id]?.points || 0,
      units: totals[p.player_id]?.units || 0,
    }))
    .sort((a, b) => (b.points - a.points) || (b.units - a.units))
  const bestPoints = ranked[0]?.points ?? 0

  return (
    <div>
      <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {ranked.map((p, i) => {
          const isLeader = p.points === bestPoints && bestPoints > 0
          return (
            <div key={p.player_id} style={{
              display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 10,
              alignItems: 'center',
              padding: '8px 12px', borderRadius: 6,
              background: isLeader ? 'rgba(212,185,122,0.12)' : 'var(--green-dark)',
              border: `1px solid ${isLeader ? 'var(--gold)' : 'var(--green-mid)'}`,
            }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                {i + 1}
              </span>
              <span style={{ fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nameFor(p.player_id)}
              </span>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '1rem', fontWeight: 700,
                color: 'var(--cream)', minWidth: 44, textAlign: 'right',
              }}>{p.points}</span>
              {p.units !== 0 && (
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: '0.8rem', fontWeight: 700,
                  color: p.units > 0 ? 'var(--blue-birdie)' : 'var(--red)',
                  minWidth: 60, textAlign: 'right',
                }}>{p.units > 0 ? '+' : ''}{p.units.toFixed(2)}</span>
              )}
              {p.units === 0 && (
                <span style={{ fontSize: '0.7rem', color: 'var(--gray-500)', minWidth: 60, textAlign: 'right' }}>
                  pts
                </span>
              )}
            </div>
          )
        })}
      </div>

      {holes.length > 0 && (
        <div>
          <p style={{ fontSize: '0.7rem', color: 'var(--gray-300)', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Points by hole
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: '100%', fontSize: '0.7rem' }}>
              <thead>
                <tr>
                  <th style={cellStyle(true)}>H</th>
                  {ranked.map(p => (
                    <th key={p.player_id} style={cellStyle(true)}>{nameFor(p.player_id).slice(0, 3)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {holes.map(h => (
                  <tr key={h.hole}>
                    <td style={cellStyle(false, true)}>{h.hole}</td>
                    {ranked.map(p => {
                      const pts = h.points[p.player_id]
                      return (
                        <td key={p.player_id} style={{
                          ...cellStyle(false),
                          background: bandFor(pts),
                          color: pts >= 3 ? 'var(--green-deep)' : 'var(--cream)',
                          fontWeight: 700,
                        }}>
                          {pts ?? '·'}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function cellStyle(header, monoLabel) {
  return {
    padding: '3px 6px',
    borderBottom: '1px solid var(--green-mid)',
    borderRight: '1px solid var(--green-mid)',
    fontFamily: monoLabel || header ? 'var(--font-mono)' : 'inherit',
    color: header ? 'var(--gray-300)' : 'var(--cream)',
    textAlign: 'center',
  }
}

// Stableford points bands — quick color scale so the grid reads at a
// glance. 0 = double or worse (dim), 1 = bogey, 2 = par, 3 = birdie
// (gold), 4+ = eagle+ (bright).
function bandFor(pts) {
  if (pts == null) return 'transparent'
  if (pts >= 4) return '#39FF14'
  if (pts === 3) return 'var(--gold)'
  if (pts === 2) return 'var(--green-dark)'
  if (pts === 1) return 'var(--green-deep)'
  return 'transparent'
}
